// Theme persistence helper.
//
// The session settings sync (/api/settings/all) can fail silently on iOS — the
// Xcode log surfaced `[SessionProvider] settings persist failed {}` when the
// network was offline at boot. To make the theme choice survive a relaunch
// regardless of network state we mirror it to a local store:
//   1) Capacitor Preferences (native iOS/Android) — survives WebView restarts.
//   2) localStorage fallback for the dev/web client.
//
// Reads return the first non-empty value found across both stores. Writes
// fan out to both stores best-effort and never throw.

const KEY = "nexus-dex.themeMode";
const VALID = new Set(["auto", "light", "dark"]);

type ThemeMode = "auto" | "light" | "dark";

function readLocalStorage(): ThemeMode | null {
  try {
    if (typeof window === "undefined") return null;
    const v = window.localStorage.getItem(KEY);
    return v && VALID.has(v) ? (v as ThemeMode) : null;
  } catch {
    return null;
  }
}

function writeLocalStorage(value: ThemeMode): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(KEY, value);
  } catch {
    /* sandboxed iframes may block storage; ignore. */
  }
}

async function readCapacitorPrefs(): Promise<ThemeMode | null> {
  try {
    const { Preferences } = await import("@capacitor/preferences" as any);
    const res = await Preferences.get({ key: KEY });
    const v = res?.value;
    return v && VALID.has(v) ? (v as ThemeMode) : null;
  } catch {
    return null;
  }
}

async function writeCapacitorPrefs(value: ThemeMode): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences" as any);
    await Preferences.set({ key: KEY, value });
  } catch {
    /* not on a Capacitor runtime; localStorage is the source of truth. */
  }
}

export function readPersistedThemeModeSync(): ThemeMode | null {
  return readLocalStorage();
}

export async function readPersistedThemeMode(): Promise<ThemeMode | null> {
  const local = readLocalStorage();
  if (local) return local;
  return await readCapacitorPrefs();
}

export function persistThemeMode(value: ThemeMode): void {
  if (!VALID.has(value)) return;
  writeLocalStorage(value);
  // Fire-and-forget Capacitor write; resolves asynchronously and never throws.
  void writeCapacitorPrefs(value);
}
